import React, { useEffect, useRef, ReactNode } from "react";
import useDetectOutsideClick from "./useDetectOutsideClick";

interface ActionDropdownProps {
  button: ReactNode;
  menuClass?: string;
  children: ReactNode;
  disabled?: boolean;
  menuForLabel?: string;
  pointerAlign?: "left" | "center" | "right";
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  button,
  menuClass = "",
  disabled = false,
  children,
  menuForLabel,
  pointerAlign = "right",
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef(false);
  const [isOpen, setIsOpen] = useDetectOutsideClick(dropdownRef, false);

  useEffect(() => {
    if (restoreFocusRef.current && !isOpen) {
      triggerRef.current?.focus();
      restoreFocusRef.current = false;
    }
  }, [isOpen]);

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    }

    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      restoreFocusRef.current = true;
      setIsOpen(false);
    }
  };

  return (
    <div className="menu-container" ref={dropdownRef}>
      <div
        ref={triggerRef}
        className={`menu-trigger ${isOpen ? "active" : ""}`}
        onClick={() => (disabled ? null : setIsOpen(!isOpen))}
        onKeyDown={handleTriggerKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {button}
      </div>

      <nav
        className={`menu menu-pointer-${pointerAlign} ${isOpen ? "active" : "inactive"} ${menuClass}`}
        role="menu"
        aria-hidden={!isOpen}
        onClickCapture={() => {
          restoreFocusRef.current = true;
          setIsOpen(false);
        }}
        onKeyDown={handleMenuKeyDown}
      >
        {menuForLabel ? (
          <>
            <div className="menu-for-label">{menuForLabel}</div>
            <hr />
          </>
        ) : null}
        {children}
      </nav>
    </div>
  );
};

export default ActionDropdown;
